/**
 * R-72 — Build corpus percentile baselines per report type.
 */
import type { ReportTypeId } from '../../config/reportTypes';
import { REPORT_TYPE_REGISTRY } from '../../config/reportTypes';
import type { SiteRagReport } from '../../types/siteRag';
import {
  computeStats,
  DISTRIBUTION_STAT_FIELDS,
  type DistributionStats,
} from './distributionStats';

export interface StatPercentiles {
  p10: number;
  p25: number;
  p50: number;
  p75: number;
  p90: number;
}

export interface DistributionBaseline {
  reportCount: number;
  stats: Record<keyof DistributionStats, StatPercentiles>;
}

export interface DistributionBaselineFile {
  generatedAt: string;
  reportCountByType: Partial<Record<ReportTypeId, number>>;
  baselines: Partial<Record<ReportTypeId, DistributionBaseline>>;
  _comment?: string;
}

function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  if (sorted.length === 1) return sorted[0]!;
  const idx = (p / 100) * (sorted.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo]!;
  return sorted[lo]! + (sorted[hi]! - sorted[lo]!) * (idx - lo);
}

function computePercentiles(values: number[]): StatPercentiles {
  const sorted = [...values].sort((a, b) => a - b);
  return {
    p10: Math.round(percentile(sorted, 10) * 100) / 100,
    p25: Math.round(percentile(sorted, 25) * 100) / 100,
    p50: Math.round(percentile(sorted, 50) * 100) / 100,
    p75: Math.round(percentile(sorted, 75) * 100) / 100,
    p90: Math.round(percentile(sorted, 90) * 100) / 100,
  };
}

/** Reasonable seed defaults when corpus is empty. */
export function defaultStatPercentiles(field: keyof DistributionStats): StatPercentiles {
  const defaults: Partial<Record<keyof DistributionStats, StatPercentiles>> = {
    wordCount: { p10: 2500, p25: 3500, p50: 5000, p75: 7000, p90: 9000 },
    charCount: { p10: 15000, p25: 22000, p50: 32000, p75: 45000, p90: 58000 },
    sectionCount: { p10: 4, p25: 6, p50: 8, p75: 10, p90: 14 },
    h2Count: { p10: 3, p25: 5, p50: 7, p75: 9, p90: 12 },
    h3Count: { p10: 5, p25: 8, p50: 12, p75: 16, p90: 22 },
    totalCitations: { p10: 8, p25: 15, p50: 25, p75: 40, p90: 60 },
    avgCitationsPerSection: { p10: 1, p25: 2, p50: 3, p75: 5, p90: 7 },
    maxCitationsPerSection: { p10: 3, p25: 5, p50: 8, p75: 12, p90: 18 },
    tableCount: { p10: 0, p25: 1, p50: 2, p75: 4, p90: 6 },
    figureCount: { p10: 0, p25: 0, p50: 1, p75: 2, p90: 4 },
    caveatCount: { p10: 0, p25: 1, p50: 2, p75: 3, p90: 5 },
  };
  return defaults[field] ?? { p10: 0, p25: 0, p50: 0, p75: 0, p90: 0 };
}

export function buildPlaceholderBaseline(reportCount = 10): DistributionBaseline {
  const stats = {} as Record<keyof DistributionStats, StatPercentiles>;
  for (const field of DISTRIBUTION_STAT_FIELDS) {
    stats[field] = defaultStatPercentiles(field);
  }
  return { reportCount, stats };
}

export function buildBaseline(
  reports: SiteRagReport[],
): Record<ReportTypeId, DistributionBaseline> {
  const byType = new Map<ReportTypeId, DistributionStats[]>();
  for (const id of Object.keys(REPORT_TYPE_REGISTRY) as ReportTypeId[]) {
    byType.set(id, []);
  }

  for (const report of reports) {
    const type = (report.reportType ?? 'feasibility') as ReportTypeId;
    if (!byType.has(type)) byType.set(type, []);
    byType.get(type)!.push(computeStats(report));
  }

  const out = {} as Record<ReportTypeId, DistributionBaseline>;
  for (const [type, statsList] of byType) {
    if (statsList.length === 0) {
      out[type] = buildPlaceholderBaseline(0);
      continue;
    }
    const stats = {} as Record<keyof DistributionStats, StatPercentiles>;
    for (const field of DISTRIBUTION_STAT_FIELDS) {
      stats[field] = computePercentiles(statsList.map((s) => s[field]));
    }
    out[type] = { reportCount: statsList.length, stats };
  }
  return out;
}

export function buildBaselineFile(reports: SiteRagReport[]): DistributionBaselineFile {
  const baselines = buildBaseline(reports);
  const reportCountByType: Partial<Record<ReportTypeId, number>> = {};
  for (const [type, baseline] of Object.entries(baselines)) {
    reportCountByType[type as ReportTypeId] = baseline.reportCount;
  }
  return {
    generatedAt: new Date().toISOString(),
    reportCountByType,
    baselines,
    _comment: 'Run `tsx scripts/audit/buildDistributionBaseline.ts` to refresh from Firestore corpus.',
  };
}
